"use client";

import Image, { StaticImageData } from "next/image";
import { useRef } from "react";
import { useParallaxTilt, useScrollFade } from "@/lib/MotionProvider";
import "@/styles/sections.css";

import choose_thumb_1 from "@/assets/img/update/bg/bg-gradient1-1.png"
import choose_thumb_3 from "@/assets/img/update/normal/1.png"

import icon_1 from "@/assets/img/update/icon/feature-icon1-1.svg"
import icon_2 from "@/assets/img/update/icon/feature-icon1-2.svg"
import icon_3 from "@/assets/img/update/icon/feature-icon1-3.svg"
import { useLanguage } from "@/utils/LanguageContext";
import GetInvolvedButton from "@/component/common/GetInvolvedButton";


type GovernanceRole = {
   id: number;
   icon: StaticImageData;
   title: string;
   desc: string;
};

const governanceRoles: GovernanceRole[] = [
   {
      id: 1,
      icon: icon_1,
      title: "Token Holders (Community)",
      desc: "All INV holders can vote on which startup projects receive funding and elect both Endorsers and the E-CEO. Voting influence is quantified as Voting Value and executed on-chain for transparency.",
   },
   {
      id: 2,
      icon: icon_2,
      title: "Endorsers (Elected Analysts)",
      desc: "Community-elected evaluators who perform due diligence, produce reports, and vote internally. When a majority of Endorsers approve a project, it proceeds to the E-CEO for final review. Endorsers are elected in real time, accountable to token holders, and are required to stake tokens.",
   },
   {
      id: 3,
      icon: icon_3,
      title: "E-CEO (Elected CEO)",
      desc: "Nominated by Endorsers and elected by token holders, the E-CEO represents the DAO in the physical world (agreements, compliance) and executes community decisions; they do not unilaterally decide funding.",
   },
];

type GovernanceHighlight = {
   id: number;
   title: string;
   desc: string;
};

const governanceHighlights: GovernanceHighlight[] = [
   {
      id: 1,
      title: "Voting mechanics:",
      desc: "Voting Value is calculated from each holder’s token stake (e.g., 0.5% of the USDT value of total tokens at voting time), capped so no single participant dominates (e.g., max ≈1% of a funding request’s soft cap), and proposals require broad participation (e.g., a minimum of ~200 positive voters) to pass. All results are tallied on-chain.",
   },
   {
      id: 2,
      title: "PoDD (Proof of Due Diligence).",
      desc: "A reward mechanism that incentivizes informed, majority-aligned voting. In the whitepaper, PoDD rewards voters whose decisions ultimately align with the majority/outcome; the patent filing formalizes this as a reward-based majority voting model with newly minted tokens distributed to majority-aligned voters.",
   },
   {
      id: 3,
      title: "End-to-end flow (condensed).",
      desc: "Startup submits → Endorsers’ due diligence & majority endorsement → E-CEO posts for community vote → Token holders vote (weighted by Voting Value) → If approved, funding is executed per terms and tracked transparently.",
   },
];

const governancePairs = governanceRoles.map((role, index) => ({
   id: role.id,
   role,
   highlight: governanceHighlights[index],
}));

const GovernanceRoleCard = ({ item }: { item: GovernanceRole }) => {
   const ref = useRef<HTMLElement>(null);
   useScrollFade(ref);
   useParallaxTilt(ref);
   const { t } = useLanguage();

   return (
      <article ref={ref} className="feature-card governance-role-card">
         <div className="feature-card-icon">
            <Image src={item.icon} alt="" aria-hidden="true" loading="lazy" />
         </div>
         <div className="feature-card-details">
            <div className="governance-card-heading">
               <span className="governance-card-number" aria-hidden="true">{item.id}</span>
               <h3 className="feature-card-title">{t(item.title)}</h3>
            </div>
            <p className="feature-card-text">{t(item.desc)}</p>
         </div>
      </article>
   );
};

const GovernanceHighlightCard = ({ item }: { item: GovernanceHighlight }) => {
   const ref = useRef<HTMLElement>(null);
   const { t } = useLanguage();
   useScrollFade(ref);

   return (
      <article ref={ref} className="governance-note-card">
         <div className="governance-card-heading">
            <span className="governance-card-number governance-card-number--note" aria-hidden="true">{item.id}</span>
            <h3 className="governance-note-title">{t(item.title)}</h3>
         </div>
         <p className="governance-note-text">{t(item.desc)}</p>
      </article>
   );
};

const GovernanceArea = () => {
   const { t } = useLanguage();
   return (
      <section
         className="wcu-area-1 pt-130 pb-140 position-relative governance-area home-anchor-target"
         id="feature"
         aria-labelledby="governance-title"
      >
         <div className="bg-gradient-1">
            <Image src={choose_thumb_1} alt="" loading="lazy" />
         </div>
         <div className="container">
            <div className="mb-25">
               <div className="row gy-5 align-items-center">
                  <div className="col-lg-7">
                     <div className="section-title mb-0 governance-intro">
                        <h2 className="title style2" id="governance-title">{t("Governance")}</h2>
                        <p className="sec-text">{t("Invtron DAO’s governance is decentralized yet structured, with clear roles and on-chain processes:")}
                        </p>
                     </div>
                  </div>
                  <div className="col-lg-5">
                     <div className="wcu-thumb text-center alltuchtopdown governance-hero-visual">
                        <Image src={choose_thumb_3} alt="" loading="lazy" />
                     </div>
                  </div>
               </div>
            </div>
            <ol className="governance-pair-list">
               {governancePairs.map(({ id, role, highlight }) => (
                  <li key={id} className="governance-pair-row">
                     <div className="governance-pair-card">
                        <GovernanceRoleCard item={role} />
                     </div>
                     <div className="governance-pair-card">
                        <GovernanceHighlightCard item={highlight} />
                     </div>
                  </li>
               ))}
            </ol>
            <GetInvolvedButton />
         </div>
      </section>
   )
}

export default GovernanceArea
